import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartConfiguration, ChartType } from 'chart.js';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { ToastrService } from 'ngx-toastr';

interface DailyUnmatchedScan {
  date: string;
  packaging_qr: number;
  scanner_prefix: number;
  unknown_ean: number;
  other: number;
  total: number;
  recoveredWithin60sPct?: number;
}

interface DailyClear {
  date: string;
  count: number;
  dkk: number;
  notRepurchasedCount: number;
  notRepurchasedDkk: number;
}

@Component({
  selector: 'app-scan-insights',
  templateUrl: './scan-insights.component.html',
  styleUrls: ['./scan-insights.component.scss']
})
export class ScanInsightsComponent implements OnInit, OnChanges {

  @Input() storeId: string = '';

  private readonly defaultWindowDays = 30;

  dateFrom = '';
  dateTo = '';

  isLoading = false;
  errorMessage = '';
  endpointUnavailable = false;

  recoverySummary: any = {};
  clears: any = {};
  topUnmatchedCodes: any[] = [];
  flaggedClears: any[] = [];
  dailyUnmatchedScans: DailyUnmatchedScan[] = [];
  dailyClears: DailyClear[] = [];

  public unmatchedChartData: ChartConfiguration['data'] = { labels: [], datasets: [] };
  public unmatchedChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: { stacked: true },
      y: { stacked: true, beginAtZero: true, title: { display: true, text: 'Unmatched scans' } }
    },
    plugins: { legend: { position: 'top' } }
  };
  public unmatchedChartType: ChartType = 'bar';

  public clearsChartData: ChartConfiguration['data'] = { labels: [], datasets: [] };
  public clearsChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: { beginAtZero: true, position: 'left', title: { display: true, text: 'Clears' } },
      y1: {
        beginAtZero: true,
        position: 'right',
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'DKK' }
      }
    },
    plugins: { legend: { position: 'top' } }
  };
  public clearsChartType: ChartType = 'bar';

  private loadSeq = 0;

  constructor(
    private ecommerceService: EcommerceService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.setDefaultDateRange();
    if (this.storeId) {
      this.loadInsights();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['storeId'] && !changes['storeId'].firstChange) {
      this.loadSeq++;
      this.resetData();
      if (this.storeId) {
        this.loadInsights();
      }
    }
  }

  private resetData(): void {
    this.recoverySummary = {};
    this.clears = {};
    this.topUnmatchedCodes = [];
    this.flaggedClears = [];
    this.dailyUnmatchedScans = [];
    this.dailyClears = [];
    this.errorMessage = '';
    this.endpointUnavailable = false;
    this.unmatchedChartData = { labels: [], datasets: [] };
    this.clearsChartData = { labels: [], datasets: [] };
  }

  private setDefaultDateRange(): void {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - this.defaultWindowDays);
    this.dateTo = to.toISOString().slice(0, 10);
    this.dateFrom = from.toISOString().slice(0, 10);
  }

  loadInsights(): void {
    const seq = ++this.loadSeq;
    const requestedStoreId = this.storeId;

    this.isLoading = true;
    this.errorMessage = '';
    this.endpointUnavailable = false;

    const params: any = {};
    if (this.dateFrom) params.from = this.dateFrom;
    if (this.dateTo) params.to = this.dateTo;

    this.ecommerceService.getScanInsights(requestedStoreId, params).subscribe({
      next: (res: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) {
          return;
        }
        const data = res?.response?.data ?? res?.data ?? {};
        this.recoverySummary = data.recoverySummary || {};
        this.clears = data.clears || {};
        this.topUnmatchedCodes = data.topUnmatchedCodes || [];
        this.flaggedClears = data.flaggedClears || [];
        this.dailyUnmatchedScans = data.dailyUnmatchedScans || [];
        this.dailyClears = this.clears.dailyClears || [];
        this.buildCharts();
        this.isLoading = false;
      },
      error: (error: any) => {
        if (seq !== this.loadSeq || requestedStoreId !== this.storeId) {
          return;
        }
        this.isLoading = false;
        if (error?.status === 404) {
          this.endpointUnavailable = true;
          this.errorMessage = 'Scan insights API is not deployed yet.';
          this.resetData();
          return;
        }
        this.errorMessage = error?.error?.response?.message || 'Failed to load scan insights';
        this.toastr.error(this.errorMessage);
      }
    });
  }

  onFilterChange(): void {
    this.loadInsights();
  }

  resetFilters(): void {
    this.setDefaultDateRange();
    this.loadInsights();
  }

  private buildCharts(): void {
    this.buildUnmatchedChart();
    this.buildClearsChart();
  }

  private buildUnmatchedChart(): void {
    const labels = this.dailyUnmatchedScans.map(d => d.date.slice(5));
    this.unmatchedChartData = {
      labels,
      datasets: [
        {
          label: 'Packaging QR',
          data: this.dailyUnmatchedScans.map(d => d.packaging_qr || 0),
          backgroundColor: 'rgba(51, 102, 255, 0.85)',
          stack: 'scans'
        },
        {
          label: 'Scanner prefix',
          data: this.dailyUnmatchedScans.map(d => d.scanner_prefix || 0),
          backgroundColor: 'rgba(0, 214, 143, 0.85)',
          stack: 'scans'
        },
        {
          label: 'Unknown EAN',
          data: this.dailyUnmatchedScans.map(d => d.unknown_ean || 0),
          backgroundColor: 'rgba(255, 170, 0, 0.85)',
          stack: 'scans'
        },
        {
          label: 'Other',
          data: this.dailyUnmatchedScans.map(d => d.other || 0),
          backgroundColor: 'rgba(108, 117, 125, 0.85)',
          stack: 'scans'
        }
      ]
    };
  }

  private buildClearsChart(): void {
    const labels = this.dailyClears.map(d => d.date.slice(5));
    this.clearsChartData = {
      labels,
      datasets: [
        {
          label: 'Clears',
          data: this.dailyClears.map(d => d.count || 0),
          backgroundColor: 'rgba(51, 102, 255, 0.6)',
          borderColor: 'rgba(51, 102, 255, 1)',
          borderWidth: 1,
          yAxisID: 'y'
        },
        {
          label: 'Not re-bought (15 min)',
          data: this.dailyClears.map(d => d.notRepurchasedCount || 0),
          backgroundColor: 'rgba(255, 61, 113, 0.7)',
          borderColor: 'rgba(255, 61, 113, 1)',
          borderWidth: 1,
          yAxisID: 'y'
        },
        {
          label: 'Cleared DKK',
          data: this.dailyClears.map(d => d.dkk || 0),
          type: 'line',
          borderColor: 'rgba(40, 167, 69, 1)',
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.2,
          yAxisID: 'y1'
        }
      ]
    };
  }

  formatCurrency(amount: number | undefined | null): string {
    if (amount == null) {
      return '—';
    }
    return new Intl.NumberFormat('da-DK', {
      style: 'currency',
      currency: 'DKK'
    }).format(amount);
  }

  formatPct(value: number | undefined | null): string {
    if (value == null) {
      return '—';
    }
    return `${Number(value).toFixed(1)}%`;
  }

  getScanKindLabel(kind: string): string {
    switch (kind) {
      case 'packaging_qr': return 'Packaging QR';
      case 'scanner_prefix': return 'Scanner prefix';
      case 'unknown_ean': return 'Unknown EAN';
      case 'other': return 'Other';
      default: return kind || '—';
    }
  }

  getScanKindBadgeClass(kind: string): string {
    switch (kind) {
      case 'packaging_qr': return 'bg-primary';
      case 'scanner_prefix': return 'bg-success';
      case 'unknown_ean': return 'bg-warning text-dark';
      case 'other': return 'bg-secondary';
      default: return 'bg-light text-dark border';
    }
  }

  getCustomerLabel(clear: any): string {
    const customer = clear?.customer;
    if (!customer) {
      return 'Anonymous';
    }
    if (customer.type === 'member' && customer.memberName) {
      return customer.memberName;
    }
    if (customer.type === 'member' && customer.memberId) {
      return `Member ${customer.memberId.slice(0, 8)}…`;
    }
    return 'Anonymous';
  }

  formatItems(items: any[]): string {
    if (!items || items.length === 0) {
      return '—';
    }
    return items.map(item => `${item.name} ×${item.quantity || 1}`).join(', ');
  }

  trackByCode(index: number, row: any): string {
    return row.code || String(index);
  }

  trackByEventId(index: number, row: any): string {
    return row.eventId || String(index);
  }
}
